/**
 * Claims rules for finished ad copy that a figure or a phrase list cannot
 * express: who is allowed to be named, how a brand's own statistic may be
 * stated, and what the copy may say about the reader.
 *
 * Pure. Each function takes the shipping fields plus the evidence classes the
 * caller already holds and returns findings; the caller decides whether a
 * finding blocks (`generate-grounded-ad-copy.ts`'s `evaluateQualityDoctrine`).
 *
 * WHY THESE EXIST (ITC Tax Brief, round 4, 2026-09-25 — three judges, 2/5):
 * every ad unit shipped "delivers an average 43% first-year reduction". The
 * qualifier check passed it, because "average" and "first-year" WERE present.
 * What the doctrine actually requires of a brand's OWN statistic is different
 * and was never coded: it is the brand's claim, so it is attributed to the
 * brand ("we report"), it is scoped as a firm-wide average with "results
 * vary", it appears once, and it never sits in a headline or a description
 * where neither the attribution nor the caveat can travel with it.
 * "Delivers"/"Get" turns the same number into a promise made to the reader.
 * The same run named the sitting governor in paid copy and quoted the article's
 * own headline as a hook; both are countable, so both are checked here.
 */

import { containsNormalized } from './normalize-for-match';
import { extractFigures } from './unevidenced-figures';

export interface AdCopyClaimsField {
  readonly field: string;
  readonly text: string;
}

/** A finding without its severity: the caller stamps `blocking`. */
export interface AdCopyClaimsFinding {
  readonly field: string;
  readonly detail: string;
  readonly fix: string;
  /**
   * The one sentence of the field that carries the defect, when there is one.
   * A claims gate that has spent its regeneration budget removes exactly this
   * sentence rather than ship it flagged.
   */
  readonly sentence?: string;
  /**
   * A replacement for `sentence` built only from the source line, when the
   * defect is one that has a mechanical, evidence-preserving fix (a results
   * figure that needs attributing). The claims gate applies it instead of
   * withholding the sentence once the regeneration budget is spent.
   */
  readonly rewrite?: string;
}

const cap = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const MAGNITUDE_WORD = /\b(?:average|median|typical(?:ly)?|on average|up to|as much as)\b/i;

/** Nouns and verbs that make a percentage a RESULT the customer is being promised. */
const OUTCOME_WORD =
  /\b(?:reduc\w*|sav(?:e|es|ed|ing|ings)|increas\w*|grow\w*|growth|improv\w*|lift|boost\w*|lower\w*|cut|cuts|return|returns|roi|revenue|profit\w*|conversion\w*|results?|gains?|recover\w*|refund\w*|decreas\w*|faster)\b/i;

const ATTRIBUTION =
  /\b(?:we\s+report(?:ed)?|we(?:'ve|\s+have)\s+reported|(?:our|its|their)\s+(?:own\s+)?(?:reported|stated)|reports?\s+(?:an?\s+|the\s+)?(?:\w+\s+){0,3}\d|reported\s+(?:an?\s+|the\s+)?(?:\w+\s+){0,3}\d|according\s+to|self[- ]reported|by\s+(?:our|its)\s+own)/i;

const VARIABILITY =
  /\b(?:results?\s+(?:may\s+|will\s+|can\s+)?vary|varies\s+by|vary\s+by|firm[- ]wide|company[- ]wide|not\s+a\s+guarantee|no\s+guarantee|individual\s+results|across\s+all\s+(?:our\s+)?clients)\b/i;

const SHORT_STANDALONE_FIELD = /^(?:headline|description)(?:\s+\d+)?$/i;

function splitSentences(text: string): string[] {
  return text.split(/(?<=[.!?])\s+(?=[A-Z0-9"'“(])/);
}

function figureRegex(figure: number): RegExp {
  const digits = String(figure).replace(/\./g, '\\.');
  return new RegExp(`(?:[$£€]\\s?${digits}\\b|\\b${digits}\\s?(?:%|percent\\b))`, 'i');
}

function sentenceCarryingFigure(figure: number, sources: readonly string[]): string | null {
  for (const source of sources) {
    if (extractFigures(source).includes(figure)) return source;
  }
  return null;
}

export interface SelfReportedStatisticContext {
  /** The brand's own claims: brand profile, catalogue, its own destination. */
  readonly brandSources: readonly string[];
  /** Everything else that is citable: the source article, customer quotes. */
  readonly otherSources: readonly string[];
  readonly brandName?: string | null;
}

/**
 * A brand's own outcome statistic ("an average 43% first-year reduction")
 * must be: attributed to the brand, scoped with a variability caveat, stated
 * once per field, and kept out of headlines and descriptions.
 */
export function findSelfReportedStatisticMisuse(
  fields: readonly AdCopyClaimsField[],
  context: SelfReportedStatisticContext,
): AdCopyClaimsFinding[] {
  const findings: AdCopyClaimsFinding[] = [];
  const brand = context.brandName?.trim() || 'the brand';

  for (const { field, text } of fields) {
    if (!text) continue;
    const seenFigures = new Set<number>();
    for (const figure of extractFigures(text)) {
      if (seenFigures.has(figure)) continue;
      seenFigures.add(figure);

      const brandSentence = sentenceCarryingFigure(figure, context.brandSources);
      if (!brandSentence) continue;
      if (sentenceCarryingFigure(figure, context.otherSources)) continue;
      const shape = figureRegex(figure);
      if (!shape.test(brandSentence)) continue;
      if (!MAGNITUDE_WORD.test(brandSentence) || !OUTCOME_WORD.test(brandSentence)) continue;
      const inField = shape.exec(text);
      if (!inField) continue;

      const label = inField[0].trim();
      if (SHORT_STANDALONE_FIELD.test(field)) {
        findings.push({
          field,
          detail: `${cap(field)} states ${brand}'s own ${label} result statistic where its attribution and its "results vary" caveat cannot travel with it.`,
          fix: `Take ${label} out of the ${field}: a ${brand}-reported statistic belongs once in the body, attributed and caveated. The ${field} says what the service is or what it does, with no figure from the brand's own results.`,
        });
        continue;
      }

      const problems: string[] = [];
      const occurrences = text.match(new RegExp(shape.source, 'gi'))?.length ?? 0;
      if (occurrences > 1) problems.push(`repeats ${label} ${occurrences} times`);
      const sentence = splitSentences(text).find((part) => shape.test(part)) ?? text;
      if (!ATTRIBUTION.test(sentence)) {
        problems.push(`states ${label} as a fact instead of as a claim ${brand} reports`);
      }
      if (!VARIABILITY.test(text)) problems.push('has no "results vary" caveat');
      if (problems.length === 0) continue;

      findings.push({
        field,
        detail: `${cap(field)} ${problems.join(', ')} — ${brand}'s own result statistic must be attributed, scoped as a firm-wide average, and caveated.`,
        fix: `In ${field}, state ${label} once, as ${brand}'s own reported figure, scoped exactly as the evidence scopes it and followed by a caveat, e.g. "${brand} reports ${label} on average (firm-wide average; results vary by client and jurisdiction)". Never "delivers", "gets you" or "you will".`,
      });
    }
  }
  return findings;
}

/* ─────────────────────── named officials in paid copy ─────────────────────── */

const OFFICE_TITLE =
  '(?:Gov\\.|Governor|Lt\\.?\\s+Gov(?:ernor|\\.)?|Sen\\.|Senator|Rep\\.|Representative|Congress(?:man|woman)|Mayor|Attorney\\s+General)';
const TITLED_NAME = new RegExp(
  `\\b${OFFICE_TITLE}\\s+((?:[A-Z][a-z'’-]+\\s+){0,2}[A-Z][a-z'’-]+)`,
  'g',
);
const ELECTION_WORD = /\b(?:re-?election|elections?|midterms?)\b/i;

function surnamesHeldInOffice(sources: readonly string[]): string[] {
  const surnames = new Set<string>();
  for (const source of sources) {
    for (const match of source.matchAll(TITLED_NAME)) {
      const words = match[1]!.trim().split(/\s+/);
      const surname = words[words.length - 1]!;
      if (surname.length >= 3) surnames.add(surname);
    }
  }
  return [...surnames];
}

/**
 * Paid copy attributes a government action to the agency or the state, never
 * to a named officeholder, and never mentions an election. Sponsored copy that
 * names the sitting governor next to a moratorium reads as a political message
 * in a feed whose ad policies treat those separately, and it dates the ad to
 * one person's term. `articleSources` is where the officeholder's name is
 * learned, so this holds for whoever the next article is about.
 */
export function findNamedOfficialsInPaidCopy(
  fields: readonly AdCopyClaimsField[],
  articleSources: readonly string[],
): AdCopyClaimsFinding[] {
  const findings: AdCopyClaimsFinding[] = [];
  const surnames = surnamesHeldInOffice(articleSources);
  const sourceMentionsElection = articleSources.some((source) => ELECTION_WORD.test(source));

  for (const { field, text } of fields) {
    if (!text) continue;
    const titled = text.match(TITLED_NAME)?.[0];
    const surname = surnames.find((name) => new RegExp(`\\b${name}\\b`).test(text));
    if (titled || surname) {
      const named = titled ?? surname!;
      findings.push({
        field,
        detail: `${cap(field)} names an elected official ("${named}") in paid copy.`,
        fix: `Rewrite ${field} so the action belongs to the agency, the state or the rule ("Texas has told TCEQ to…"), not to a named person. No officeholder, candidate or election appears in paid copy.`,
      });
      continue;
    }
    if (sourceMentionsElection && ELECTION_WORD.test(text)) {
      findings.push({
        field,
        detail: `${cap(field)} refers to an election in paid copy.`,
        fix: `Rewrite ${field} without any reference to an election or a campaign; state the rule and what it puts at stake.`,
      });
    }
  }
  return findings;
}

/* ─────────────────────── the article's headline as a hook ─────────────────────── */

const RUN_WORDS = 6;

function words(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * An article's headline is a title, not a sentence a reader can act on. Pasting
 * it in quotation marks and continuing the sentence ("…across your facility
 * schedules") produced a fragment. Six consecutive words shared with the title
 * is the countable version of "the copy leads with the headline".
 */
export function findSourceHeadlineRestated(
  fields: readonly AdCopyClaimsField[],
  sourceTitle: string | null | undefined,
): AdCopyClaimsFinding[] {
  const titleWords = words(sourceTitle ?? '');
  if (titleWords.length < RUN_WORDS) return [];
  const runs = new Set<string>();
  for (let start = 0; start + RUN_WORDS <= titleWords.length; start++) {
    runs.add(titleWords.slice(start, start + RUN_WORDS).join(' '));
  }
  const findings: AdCopyClaimsFinding[] = [];
  for (const { field, text } of fields) {
    if (!text) continue;
    const fieldWords = words(text);
    let hit = false;
    for (let start = 0; start + RUN_WORDS <= fieldWords.length && !hit; start++) {
      hit = runs.has(fieldWords.slice(start, start + RUN_WORDS).join(' '));
    }
    if (!hit) continue;
    findings.push({
      field,
      detail: `${cap(field)} reuses the source article's headline as its own words.`,
      fix: `Rewrite ${field} to state one fact from the article body in a sentence of its own; never open on, or quote, the article's headline.`,
    });
  }
  return findings;
}

/* ─────────────────────── a source's hedge, dropped ─────────────────────── */

/* "about" and "over" count only directly before a number ("about 86%", "over 90%"), so "a post about agents" and
   "over the past year" are not hedges. Without them, a line that keeps the source's "roughly 86%" as "about 86%", or
   "more than 90%" as "over 90%", read as having dropped the hedge. */
const QUANTITY_HEDGE =
  /\b(?:as\s+few\s+as|as\s+many\s+as|up\s+to|at\s+least|at\s+most|roughly|approximately|about(?=\s+[$€£]?\d)|over(?=\s+[$€£]?\d)|nearly|almost|more\s+than|fewer\s+than|less\s+than|estimated|an\s+average|on\s+average|average)\b/i;
const EPISTEMIC_HEDGE =
  /\b(?:appears?\s+to|appeared\s+to|seems?\s+to|seemed\s+to|reportedly|allegedly|is\s+said\s+to|are\s+said\s+to|(?:may|might|could)\s+have)\b/i;

const CLAIM_WORD_MIN_LENGTH = 4;

function stemWord(word: string): string {
  const stripped = word.replace(/(?:ing|ed|es|s|e)$/, '');
  return stripped.length >= 4 ? stripped : word;
}

function claimWords(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .split(/\s+/)
      .filter((word) => word.length >= CLAIM_WORD_MIN_LENGTH)
      .map(stemWord),
  );
}

/**
 * EVERY source sentence a copy sentence restates. The hedge and condition checks read all of
 * them: a short source sentence that shares four words outscores the long one that carries the
 * hedge, and the best-scoring sentence alone hid "may have missed" behind "We do not contact
 * assessors without your authorization" (ITC run 10).
 */
function restatedSourceSentences(sentence: string, sources: readonly string[]): string[] {
  const own = claimWords(sentence);
  if (own.size < 3) return [];
  const ownFigures = new Set(extractFigures(sentence));
  const found: string[] = [];
  for (const source of sources) {
    for (const candidate of splitSentences(source)) {
      const theirs = claimWords(candidate);
      if (theirs.size < 3) continue;
      const shared = [...own].filter((word) => theirs.has(word)).length;
      const sharesFigure = extractFigures(candidate).some((figure) => ownFigures.has(figure));
      const score = shared / Math.min(own.size, theirs.size);
      const restates = (shared >= 3 && score >= 0.5) || (sharesFigure && shared >= 2);
      if (restates && !found.includes(candidate)) found.push(candidate);
    }
  }
  return found;
}

/** "without your authorization": a condition the source puts on a commitment. */
const SOURCE_CONDITION =
  /\bwithout\s+(?:your|the|their|prior|written)\s+(?:[\w'’-]+\s+)?([\w’'-]{5,})/i;

/**
 * The condition a source puts on its own commitment ("We do not contact assessors without your
 * authorization"), when the copy sentence restates the commitment without the condition
 * ("without … contacting assessors" promises never to, which the source does not).
 */
function conditionDropped(source: string, sentence: string): string | undefined {
  const match = SOURCE_CONDITION.exec(source);
  if (!match) return undefined;
  const own = claimWords(sentence);
  const commitment = claimWords(source.slice(0, match.index));
  if ([...commitment].filter((word) => own.has(word)).length < 2) return undefined;
  if (own.has(stemWord(match[1]!.toLowerCase()))) return undefined;
  return match[0].trim().toLowerCase();
}

/**
 * The hedge in the clause of `source` that `sentence` restates, or undefined.
 * A long source sentence hedges one clause ("after as few as 28% responded")
 * and asserts another ("the governor directed…"); a copy sentence restating
 * the second owes nothing to the first's hedge.
 */
function hedgeInRestatedClause(
  hedge: RegExp,
  source: string,
  sentence: string,
  /**
   * A verb hedge ("may have missed") qualifies what follows it, not the whole clause. A copy sentence
   * that restates the clause's subject ("We surface opportunities") but never the hedged predicate
   * ("missed") dropped nothing (ITC run 9, C2). Quantity hedges qualify a figure and keep the
   * whole-clause test.
   */
  scopeAfterHedge = false,
): string | undefined {
  const own = claimWords(sentence);
  const ownFigures = new Set(extractFigures(sentence));
  for (const clause of source.split(/[,;]/)) {
    const match = hedge.exec(clause);
    const found = match?.[0];
    if (!found) continue;
    if (scopeAfterHedge) {
      const hedged = claimWords(clause.slice(match.index + found.length));
      if (![...hedged].some((word) => own.has(word))) continue;
    }
    const shared = [...claimWords(clause)].filter((word) => own.has(word)).length;
    const sharesFigure = extractFigures(clause).some((figure) => ownFigures.has(figure));
    if (shared >= 2 || sharesFigure) return found;
  }
  return undefined;
}

const DIRECTIVE_VERB = new RegExp(
  "\\b(?:directed|ordered|instructed|told|asked|urged)\\s+(?:the\\s+|an?\\s+)?[\\w'’.-]+(?:\\s+[\\w'’.-]+){0,5}?\\s+to\\s+([a-z]{4,})\\b",
  'gi',
);
const DIRECTIVE_MARKER =
  /\b(?:directed|ordered|instructed|told|asked|urged|called\s+on|plans?\s+to|will|must|should|would)\b/i;

/** The verb in every "X directed Y to <verb>" of a source, with the sentence it came from. */
function directivesIn(sources: readonly string[]): { verb: string; sentence: string }[] {
  const found: { verb: string; sentence: string }[] = [];
  for (const source of sources) {
    for (const sentence of splitSentences(source)) {
      for (const match of sentence.matchAll(DIRECTIVE_VERB)) {
        found.push({ verb: match[1]!.toLowerCase(), sentence });
      }
    }
  }
  return found;
}

/**
 * A hedge or a directive in the source, restated flat. "As few as 28%" is not
 * "only 28%", "appears to address" is not "addresses", and "directed the water
 * board to penalize" is not "now penalizes": each one turns what the source
 * hedged or only ordered into a stronger claim the source never made. The
 * number-and-token checks pass all three, because the figure and the words are
 * all present.
 */
export function findDroppedHedges(
  fields: readonly AdCopyClaimsField[],
  articleSources: readonly string[],
): AdCopyClaimsFinding[] {
  if (articleSources.length === 0) return [];
  const findings: AdCopyClaimsFinding[] = [];
  const directives = directivesIn(articleSources);
  for (const { field, text } of fields) {
    if (!text) continue;
    for (const sentence of splitSentences(text)) {
      const done = directives.find(({ verb, sentence: origin }) => {
        if (DIRECTIVE_MARKER.test(sentence.replace(/\bnon-/gi, ''))) return false;
        const echoes = claimWords(sentence).has(stemWord(verb));
        if (!echoes) return false;
        const shared = [...claimWords(sentence)].filter((word) => claimWords(origin).has(word));
        return shared.length >= 2;
      });
      if (done) {
        findings.push({
          field,
          detail: `${cap(field)} states as done ("${sentence.trim()}") what the source only says was ordered ("${done.verb}").`,
          fix: `In ${field}, say who directed whom to do what ("…directed the agency to ${done.verb}…"), not that the action is already under way.`,
          sentence: sentence.trim(),
        });
        continue;
      }

      const restated = restatedSourceSentences(sentence, articleSources);
      if (restated.length === 0) continue;

      let droppedQuantity: string | null = null;
      let droppedEpistemic: string | null = null;
      for (const source of restated) {
        const quantity = hedgeInRestatedClause(QUANTITY_HEDGE, source, sentence);
        const epistemic = hedgeInRestatedClause(EPISTEMIC_HEDGE, source, sentence, true);
        // A quantity hedge qualifies a figure: a sentence with no figure (a headline that merely
        // names the topic) has no quantity to have stated flat.
        droppedQuantity ??=
          quantity && !QUANTITY_HEDGE.test(sentence) && extractFigures(sentence).length > 0
            ? quantity
            : null;
        droppedEpistemic ??= epistemic && !EPISTEMIC_HEDGE.test(sentence) ? epistemic : null;
      }
      const droppedCondition = restated
        .map((source) => conditionDropped(source, sentence))
        .find(Boolean);
      if (droppedCondition) {
        findings.push({
          field,
          detail: `${cap(field)} drops the source's condition "${droppedCondition}": "${sentence.trim()}".`,
          fix: `Keep the source's condition in ${field} ("${droppedCondition}") or leave the sentence out. "We do not contact assessors without your authorization" is not "without contacting assessors".`,
          sentence: sentence.trim(),
        });
      }
      const dropped = droppedQuantity ?? droppedEpistemic;
      if (dropped) {
        findings.push({
          field,
          detail: `${cap(field)} drops the source's hedge "${dropped.toLowerCase()}": "${sentence.trim()}".`,
          fix: `Keep the source's own hedge in ${field} ("${dropped.toLowerCase()}") or leave the sentence out. "As few as 28%" is not "only 28%", and "appears to address" is not "addresses".`,
          sentence: sentence.trim(),
        });
        continue;
      }
    }
  }
  return findings;
}

/* ─────────────────────── an offer the brand never made ─────────────────────── */

const OFFER_NOUN =
  'review|audit|assessment|consultation|consult|analysis|memo|report|evaluation|second\\s+opinion|briefing|walkthrough|checklist|guide|whitepaper|playbook|demo|strategy\\s+session|health\\s*check';
const OFFER_PHRASE = new RegExp(`\\b((?:[a-z][a-z-]+\\s+){0,3}?(?:${OFFER_NOUN}))\\b`, 'gi');
const CTA_VERB =
  /\b(?:contact|book|request|schedule|get|claim|download|start|ask\s+for|talk\s+to|consult|call|reach\s+out|order|receive|arrange)\b/i;
/** "we do not contact assessors" states a limit; it is not a call to action. */
const NEGATED_CTA_VERB =
  /\b(?:do(?:es)?\s+not|don['’]t|doesn['’]t|never|will\s+not|won['’]t|cannot|can['’]t)\s+(?:contact|book|request|schedule|get|claim|download|start|ask\s+for|talk\s+to|consult|call|reach\s+out|order|receive|arrange)\b/gi;
const OFFER_FILLER = new Set([
  'a',
  'an',
  'the',
  'your',
  'our',
  'for',
  'to',
  'of',
  'and',
  'or',
  'with',
  'free',
  'this',
  'that',
  'contact',
  'book',
  'request',
  'schedule',
  'get',
  'claim',
  'download',
  'start',
  'ask',
  'talk',
  'consult',
  'call',
  'reach',
  'out',
  'order',
  'receive',
  'arrange',
  'now',
  'today',
  'us',
  'team',
  'in',
  'on',
  'at',
  'by',
  'from',
  'about',
  'their',
  'its',
  'as',
  'is',
  'be',
  'can',
  'will',
  'tax',
  'itc',
  'first',
  'new',
  'more',
  'next',
]);

/**
 * A call to action that names a deliverable ("a private valuation review", "a
 * findings memo") must name one the brand's own text describes. Ad copy is
 * written to a brand's real offer; a model that coins a plausible-sounding
 * service invents a promise the brand may not keep. Every word of the offer
 * phrase must appear in the brand's own text, except for connectives and the
 * verb that asks for it. A phrase that appears word for word (typography
 * ignored) in a cited source line is that source's own offer, and passes.
 */
export function findInventedOffers(
  fields: readonly AdCopyClaimsField[],
  brandSources: readonly string[],
  brandName?: string | null,
  citedSources: readonly string[] = [],
): AdCopyClaimsFinding[] {
  if (brandSources.length === 0) return [];
  const brandWords = claimWords(brandSources.join(' '));
  const brandNameWords = claimWords(brandName ?? '');
  const findings: AdCopyClaimsFinding[] = [];
  for (const { field, text } of fields) {
    if (!text || SHORT_STANDALONE_FIELD.test(field)) continue;
    for (const sentence of splitSentences(text)) {
      if (!CTA_VERB.test(sentence.replace(NEGATED_CTA_VERB, ' '))) continue;
      for (const match of sentence.matchAll(OFFER_PHRASE)) {
        const phrase = match[1]!.trim();
        if (citedSources.some((source) => containsNormalized(source, phrase))) continue;
        const modifiers = phrase
          .toLowerCase()
          .split(/\s+/)
          .filter((word) => !OFFER_FILLER.has(word) && !brandNameWords.has(stemWord(word)));
        const invented = modifiers.filter((word) => !brandWords.has(stemWord(word)));
        if (invented.length === 0) continue;
        findings.push({
          field,
          detail: `${cap(field)} offers "${phrase}", and the brand's own text never describes that offer ("${invented[0]}" appears nowhere in it).`,
          fix: `In ${field}, offer only what the brand's own text describes, in its own words (its actual service or review), or simply invite the reader to contact the brand. Do not name a deliverable, report or review the brand's text does not.`,
          sentence: sentence.trim(),
        });
        break;
      }
    }
  }
  return findings;
}

/* ─────────────────────── two asks in one ad ─────────────────────── */

const ENGAGE_ASK =
  /\b(?:share\s+your|comment\s+(?:below|your)|in\s+the\s+comments|tell\s+us|let\s+us\s+know|reply\s+(?:below|with)|drop\s+a\s+comment|below)\b/i;
const CONVERT_ASK =
  /\b(?:contact|consult|book|request|schedule|download|call\s+(?:us|the)|talk\s+to|reach\s+out|speak\s+(?:to|with)|get\s+in\s+touch)\b/i;

/**
 * A paid ad has ONE ask. "Share your experiences below or consult the firm"
 * asks for a comment and a conversation at once, and "below" belongs to an
 * organic post; the reader who does neither is the usual outcome.
 */
export function findCompetingAsks(fields: readonly AdCopyClaimsField[]): AdCopyClaimsFinding[] {
  const findings: AdCopyClaimsFinding[] = [];
  for (const { field, text } of fields) {
    if (!text || SHORT_STANDALONE_FIELD.test(field)) continue;
    if (ENGAGE_ASK.test(text) && CONVERT_ASK.test(text)) {
      findings.push({
        field,
        detail: `${cap(field)} makes two asks at once (comment and contact).`,
        fix: `Give ${field} one ask, the offer. Remove the request to comment, share or reply.`,
      });
    }
  }
  return findings;
}

/* ─────────────────────── diagnosing the reader ─────────────────────── */

const READER_DIAGNOSIS =
  /\byour\s+(?:[\w-]+\s+){1,6}(?:is|are|remains?|remain)\s+(?:completely\s+|totally\s+|entirely\s+|now\s+)?(?:in\s+limbo|up\s+in\s+the\s+air|stalled|frozen|on\s+hold|at\s+risk|inflated|overstated|overvalued|broken|exposed)\b/i;

/**
 * Copy that tells the reader what their own situation is ("your valuation
 * models are completely up in the air") asserts a fact nothing on file can
 * know. A persona's pain language is a register to write in, never a
 * statement to print.
 */
export function findReaderDiagnosis(fields: readonly AdCopyClaimsField[]): AdCopyClaimsFinding[] {
  const findings: AdCopyClaimsFinding[] = [];
  for (const { field, text } of fields) {
    const match = text ? READER_DIAGNOSIS.exec(text) : null;
    if (!match) continue;
    findings.push({
      field,
      detail: `${cap(field)} asserts the reader's own situation ("${match[0]}") — nothing on file knows it.`,
      fix: `Rewrite ${field} to state what the source says and what it puts at stake for someone in the reader's role, without telling the reader what state their own assets or plans are in.`,
      sentence: splitSentences(text)
        .find((part) => READER_DIAGNOSIS.test(part))
        ?.trim(),
    });
  }
  return findings;
}
